import { useCallback, useState, useEffect, useRef } from 'react';
import { SpamReport, useGetSpamReportQuery } from './useGetSpamReportQuery';
import { useRetestSpamReportQuery } from './useRetestSpamReportQuery';
import { addNegativeToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';

interface UseSpamRetestAndPollOptions {
  pollingInterval?: number;
  pollingDuration?: number;
  enableInitialData?: boolean;
}

export const SPAM_RETEST_AND_POLL_ERROR_MESSAGES = {
  retest: 'Unable to regenerate spam report. Please try again later.',
};

export const useSpamRetestAndPoll = (
  templateId?: number,
  options?: UseSpamRetestAndPollOptions,
) => {
  const dispatch = useDispatch();
  const [isPolling, setIsPolling] = useState(false);
  const [spamReports, setSpamReports] = useState<SpamReport[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    pollingInterval = 8000,
    pollingDuration = 80000,
    enableInitialData = false,
  } = options || {};

  const {
    refetch: retestFn,
    isLoading: isLoadingRetest,
    isError: isRetestError,
  } = useRetestSpamReportQuery(templateId, {
    enabled: false,
  });

  const {
    data: spamReportData,
    isLoading: isLoadingSpamReports,
    isError: isSpamReportError,
  } = useGetSpamReportQuery(templateId, {
    enabled: isPolling || (!!templateId && enableInitialData),
    refetchInterval: isPolling ? pollingInterval : undefined,
  });

  // Watch for spam report results
  useEffect(() => {
    if ((isPolling || enableInitialData) && spamReportData) {
      setSpamReports(spamReportData);
    }
  }, [isPolling, enableInitialData, spamReportData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const startRetestAndPolling = useCallback(async () => {
    if (!templateId) {
      dispatch(addNegativeToast(SPAM_RETEST_AND_POLL_ERROR_MESSAGES.retest));
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      const result = await retestFn();

      setIsPolling(true);
      setSpamReports([]);

      timeoutRef.current = setTimeout(() => {
        setIsPolling(false);
      }, pollingDuration);
    } catch (err) {
      dispatch(addNegativeToast(SPAM_RETEST_AND_POLL_ERROR_MESSAGES.retest));
    }
  }, [templateId, retestFn, pollingDuration, dispatch]);

  const cancelPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  return {
    startRetestAndPolling,
    isPolling,
    cancelPolling,
    spamReports,
    isRetestError,
    isSpamReportError,
    isLoading: isLoadingRetest || isLoadingSpamReports,
  };
};
